import { CardDetailCreateResponse, GroupCardCreatePayload } from '../interfaces/group-card';
import { CommonResponse } from '../interfaces/categories';
import axiosInstance from '../axios';
import { AxiosError } from 'axios';

export type GroupCardInviteStatus = 'invited' | 'signed';

export interface GroupCardInvite {
  id: string;
  email: string;
  name: string | null;
  status: GroupCardInviteStatus;
  invited_at: string;
  last_reminded_at: string | null;
  reminder_count: number;
}

export interface GroupCardInviteInput {
  email: string;
  name?: string;
}

/** Response shape that also carries the HTTP status so callers can react to 400/429. */
export type GroupCardServiceResponse = CommonResponse & { http_status?: number };

/**
 * Normalise an axios error into the standard response envelope, preserving the
 * backend's message (DRF `response_message`/`message`/`detail`) and HTTP status.
 */
const toErrorResponse = (error: unknown, fallbackData: any = null): GroupCardServiceResponse => {
  if (error instanceof AxiosError) {
    const data: any = error.response?.data;
    const backendMessage =
      (data && (data.response_message || data.message || data.detail)) ||
      error.message;
    return {
      http_status: error.response?.status,
      response_status: false,
      response_message: typeof backendMessage === 'string' ? backendMessage : 'Something went wrong',
      response_data: data?.response_data ?? fallbackData,
    };
  }
  return {
    response_status: false,
    response_message: error instanceof Error ? error.message : 'Unknown error',
    response_data: fallbackData,
  };
};

/**
 * Service for group card creation and management
 */
export const GroupCardService = {
  /**
   * Get card details for creation
   * @param id - Card ID
   * @returns Promise with the card details
   */
  getCardCreateDetails: async (id: string): Promise<CardDetailCreateResponse> => {
    try {
      const response = await axiosInstance.get(`/cards/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching card details:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: {
          card_image: '',
          category: '',
          id: '',
          is_active: false,
          is_staff_pick: false,
          name: '',
          position: 0,
          description: '',
        }
      };
    }
  },

  /**
   * Create a group card
   * @param payload - Group card creation payload
   * @returns Promise with the creation response
   */
  createCard: async (payload: GroupCardCreatePayload): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.post('/group-cards/create/', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating card:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Upload an image for the card
   * @param formData - Form data with the image
   * @returns Promise with the upload response
   */
  uploadImage: async (formData: FormData): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.post('/master/images/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading image:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Get music list
   * @returns Promise with the music list
   */
  getMusicList: async (): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.get('/master/music/');
      return response.data;
    } catch (error) {
      console.error('Error fetching music list:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: []
      };
    }
  },

  /**
   * Update a group card
   * @param id - Card ID
   * @param payload - Group card update payload
   * @returns Promise with the update response
   */
  updateCard: async (id: string, payload: any): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.patch(`/group-cards/${id}/update/`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating card:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Create a bulk card order batch
   * @param payload - Bulk order creation payload
   */
  createBulkOrder: async (payload: any): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.post('/group-cards/bulk-order/create/', payload);
      return response.data;
    } catch (error) {
      console.error('Error creating bulk card order:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Get user bulk card orders
   */
  getUserBulkOrders: async (): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.get('/profile/bulk-orders/');
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk card orders:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: []
      };
    }
  },

  /**
   * Get detail of a specific user bulk card order
   * @param id - Bulk order ID
   */
  getUserBulkOrderDetail: async (id: string): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.get(`/profile/bulk-orders/${id}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk card order detail:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Delete a bulk card order and all its linked cards
   * @param id - Bulk order ID
   */
  deleteBulkOrder: async (id: string): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.delete(`/group-cards/bulk-order/${id}/delete/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting bulk card order:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Update bulk card order details (e.g. title, description)
   * @param id - Bulk order ID
   * @param payload - Updated order details
   */
  updateBulkOrder: async (id: string, payload: { collection_title?: string; collection_description?: string; [key: string]: any }): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.patch(`/group-cards/bulk-order/${id}/update/`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating bulk card order:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: null
      };
    }
  },

  /**
   * Get card details for creation
   * @param slug - Card slug
   * @returns Promise with the card details
   */
  getCardCreateDetailsBySlug: async (slug: string): Promise<CardDetailCreateResponse> => {
    try {
      const response = await axiosInstance.get(`/cards/slug/${encodeURIComponent(slug)}/`);
      return response.data;
    } catch (error) {
      const httpStatus = error instanceof AxiosError ? error.response?.status : undefined;
      if (httpStatus !== 404) {
        console.error('Error fetching card details:', error);
      }
      return {
        http_status: httpStatus,
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Unknown error',
        response_data: {
          card_image: '',
          category: '',
          id: '',
          is_active: false,
          is_staff_pick: false,
          name: '',
          position: 0,
          description: '',
        }
      };
    }
  },

  /**
   * List email invites for a group card (owner only)
   */
  getInvites: async (cardId: string): Promise<GroupCardServiceResponse> => {
    try {
      const response = await axiosInstance.get(`/group-cards/${cardId}/invites/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invites:', error);
      return toErrorResponse(error, []);
    }
  },

  /**
   * Invite contributors by email (owner only). Backend dedupes by email and returns the full list.
   */
  addInvites: async (
    cardId: string,
    invites: GroupCardInviteInput[],
    message?: string
  ): Promise<GroupCardServiceResponse> => {
    try {
      const body: { invites: GroupCardInviteInput[]; message?: string } = { invites };
      if (message && message.trim()) body.message = message.trim();
      const response = await axiosInstance.post(`/group-cards/${cardId}/invites/`, body);
      return response.data;
    } catch (error) {
      console.error('Error adding invites:', error);
      return toErrorResponse(error);
    }
  },

  /**
   * Remind invitees who haven't signed. Empty/omitted inviteIds = everyone still 'invited'.
   * Response data: { reminded, skipped }
   */
  remindInvites: async (cardId: string, inviteIds: string[] = []): Promise<GroupCardServiceResponse> => {
    try {
      const response = await axiosInstance.post(`/group-cards/${cardId}/invites/remind/`, { invite_ids: inviteIds });
      return response.data;
    } catch (error) {
      console.error('Error sending reminders:', error);
      return toErrorResponse(error);
    }
  },

  /**
   * Remove an invite (owner only)
   */
  deleteInvite: async (cardId: string, inviteId: string): Promise<GroupCardServiceResponse> => {
    try {
      const response = await axiosInstance.delete(`/group-cards/${cardId}/invites/${inviteId}/`);
      // DELETE may return 204 with no body
      return response.data || { response_status: true, response_message: 'Invite removed', response_data: null };
    } catch (error) {
      console.error('Error deleting invite:', error);
      return toErrorResponse(error);
    }
  },

  /**
   * Mark a sent card as viewed by the recipient (public; ignored for the owner)
   */
  markViewed: async (cardId: string): Promise<GroupCardServiceResponse> => {
    try {
      const response = await axiosInstance.post(`/group-cards/${cardId}/viewed/`);
      return response.data;
    } catch (error) {
      return toErrorResponse(error);
    }
  },

  /**
   * Recipient sends one thank-you note to everyone who signed (public, only once)
   */
  sendThankYou: async (cardId: string, message: string, senderName?: string): Promise<GroupCardServiceResponse> => {
    try {
      const body: { message: string; sender_name?: string } = { message };
      if (senderName && senderName.trim()) body.sender_name = senderName.trim();
      const response = await axiosInstance.post(`/group-cards/${cardId}/thank-you/`, body);
      return response.data;
    } catch (error) {
      console.error('Error sending thank-you:', error);
      return toErrorResponse(error);
    }
  },

  /**
   * Set or clear the signing deadline (ISO string or null)
   */
  updateSigningDeadline: async (cardId: string, signingDeadline: string | null): Promise<GroupCardServiceResponse> => {
    try {
      const response = await axiosInstance.patch(`/group-cards/${cardId}/update/`, { signing_deadline: signingDeadline });
      return response.data;
    } catch (error) {
      console.error('Error updating signing deadline:', error);
      return toErrorResponse(error);
    }
  },

};
